import {
  BookOpen,
  CalendarDays,
  Home,
  Layers3,
  MoreHorizontal,
  PanelLeftClose,
  PanelLeftOpen,
  Settings,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import { useSuspenseQuery } from "@tanstack/react-query";

import { sessionQueryOptions } from "../features/auth/session.js";
import { BookmarkSection } from "../features/bookmarks/bookmark-section.js";
import { Logo } from "./logo.js";
import { StudyTimer } from "./study-timer.js";

type PrimaryDestination = "today" | "calendar" | "library" | "flashcards" | "settings";

export type SettingsRouteState = {
  returnTo?: string;
};

export function AppShell({
  mainClassName,
  sidebarCollapsible = false,
  children,
}: {
  mainClassName: string;
  sidebarCollapsible?: boolean;
  children: React.ReactNode;
}): React.JSX.Element {
  const { data: session } = useSuspenseQuery(sessionQueryOptions);
  const location = useLocation();
  const activeDestination = destinationForRoute(location.pathname);
  const tabletShell = useTabletShell();
  const [sidebarVisible, setSidebarVisible] = useState(true);
  const main = useRef<HTMLElement | null>(null);
  const currentPath = `${location.pathname}${location.search}`;

  const focusMain = useCallback(() => {
    main.current?.focus();
    main.current?.scrollIntoView();
  }, []);

  if (!session) return <></>;

  const settingsState: SettingsRouteState = { returnTo: currentPath };
  return (
    <div
      className={[
        "app-shell",
        sidebarCollapsible ? "sidebar-collapsible" : "",
        sidebarCollapsible && !sidebarVisible ? "sidebar-collapsed" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      data-testid="private-app"
    >
      <a
        className="skip-link"
        href="#main-content"
        onClick={(event) => {
          event.preventDefault();
          focusMain();
        }}
        onKeyDown={(event) => {
          if (event.key !== "Enter") return;
          event.preventDefault();
          focusMain();
        }}
      >
        Skip to main content
      </a>

      <aside
        id="desktop-sidebar"
        className="sidebar"
        aria-label="MemoKite sidebar"
        hidden={sidebarCollapsible && !sidebarVisible}
      >
        <Link className="brand-link" to="/today" aria-label="MemoKite Today">
          <Logo />
        </Link>
        <PrimaryNavigation activeDestination={activeDestination} />
        <BookmarkSection />
        {!tabletShell ? <StudyTimer /> : null}
        <div className="profile">
          <span className="avatar" aria-hidden="true">
            FD
          </span>
          <div>
            <strong>{session.user.displayName}</strong>
            <span>Curious learner</span>
          </div>
          <Link
            className="icon-button"
            to="/settings"
            state={settingsState}
            aria-label={`Open Settings for ${session.user.displayName}`}
          >
            <MoreHorizontal size={19} />
          </Link>
        </div>
      </aside>

      {sidebarCollapsible ? (
        <button
          type="button"
          className="sidebar-visibility-control"
          aria-label={sidebarVisible ? "Hide sidebar" : "Show sidebar"}
          aria-expanded={sidebarVisible}
          aria-controls="desktop-sidebar"
          onClick={() => setSidebarVisible((visible) => !visible)}
        >
          {sidebarVisible ? (
            <PanelLeftClose size={18} aria-hidden="true" />
          ) : (
            <PanelLeftOpen size={18} aria-hidden="true" />
          )}
        </button>
      ) : null}

      {tabletShell ? (
        <header className="tablet-app-bar" aria-label="Tablet tools">
          <Link className="brand-link" to="/today" aria-label="MemoKite Today">
            <Logo />
          </Link>
          <StudyTimer />
          <Link
            className="tablet-profile"
            to="/settings"
            state={settingsState}
            aria-label={`Open Settings for ${session.user.displayName}`}
          >
            <span className="avatar" aria-hidden="true">
              FD
            </span>
            <span>
              <strong>{session.user.displayName}</strong>
              <small>Settings</small>
            </span>
            <MoreHorizontal size={19} aria-hidden="true" />
          </Link>
        </header>
      ) : null}

      <MobileBottomNavigation activeDestination={activeDestination} settingsState={settingsState} />

      <main id="main-content" ref={main} className={mainClassName} tabIndex={-1}>
        {children}
      </main>
    </div>
  );
}

function useTabletShell(): boolean {
  const query = "(min-width: 641px) and (max-width: 980px)";
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);

  useEffect(() => {
    const media = window.matchMedia(query);
    const update = (): void => setMatches(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  return matches;
}

function PrimaryNavigation({
  activeDestination,
}: {
  activeDestination: PrimaryDestination | null;
}): React.JSX.Element {
  return (
    <nav aria-label="Primary navigation">
      <ShellNavLink
        to="/today"
        label="Today"
        icon={<Home size={19} />}
        active={activeDestination === "today"}
      />
      <ShellNavLink
        to="/calendar"
        label="Calendar"
        icon={<CalendarDays size={19} />}
        active={activeDestination === "calendar"}
      />
      <ShellNavLink
        to="/library"
        label="Library"
        icon={<BookOpen size={19} />}
        active={activeDestination === "library"}
      />
      <ShellNavLink
        to="/flashcards"
        label="Flash Cards"
        icon={<Layers3 size={19} />}
        active={activeDestination === "flashcards"}
      />
    </nav>
  );
}

function ShellNavLink({
  to,
  label,
  icon,
  active,
}: {
  to: string;
  label: string;
  icon: React.ReactNode;
  active: boolean;
}): React.JSX.Element {
  return (
    <Link
      to={to}
      className={active ? "active" : undefined}
      aria-current={active ? "page" : undefined}
    >
      {icon}
      {label}
    </Link>
  );
}

function MobileBottomNavigation({
  activeDestination,
  settingsState,
}: {
  activeDestination: PrimaryDestination | null;
  settingsState: SettingsRouteState;
}): React.JSX.Element {
  return (
    <nav className="mobile-bottom-nav" aria-label="Mobile navigation">
      <ShellNavLink
        to="/today"
        label="Today"
        icon={<Home size={23} aria-hidden="true" />}
        active={activeDestination === "today"}
      />
      <ShellNavLink
        to="/calendar"
        label="Calendar"
        icon={<CalendarDays size={23} aria-hidden="true" />}
        active={activeDestination === "calendar"}
      />
      <ShellNavLink
        to="/library"
        label="Library"
        icon={<BookOpen size={23} aria-hidden="true" />}
        active={activeDestination === "library"}
      />
      <ShellNavLink
        to="/flashcards"
        label="Flash Cards"
        icon={<Layers3 size={23} aria-hidden="true" />}
        active={activeDestination === "flashcards"}
      />
      <Link
        to="/settings"
        state={settingsState}
        className={activeDestination === "settings" ? "active" : undefined}
        aria-current={activeDestination === "settings" ? "page" : undefined}
      >
        <Settings size={23} aria-hidden="true" />
        Settings
      </Link>
    </nav>
  );
}

function destinationForRoute(pathname: string): PrimaryDestination | null {
  if (pathname === "/today") return "today";
  if (pathname === "/calendar") return "calendar";
  if (pathname === "/library" || pathname.startsWith("/activities/")) return "library";
  if (pathname === "/flashcards") return "flashcards";
  if (pathname === "/settings") return "settings";
  return null;
}
